import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Refresh as ResetIcon,
} from '@mui/icons-material';
import {
  FileCategory,
  FileFilters,
  CATEGORY_ICONS,
  CATEGORY_COLORS,
  UploaderInfo,
} from '../../types/file.types';

/**
 * FileFilterPanel Props
 */
interface FileFilterPanelProps {
  /** 현재 필터 상태 */
  filters: FileFilters;
  /** 필터 변경 핸들러 */
  onChange: (filters: FileFilters) => void;
  /** 선택 가능한 업로더 목록 */
  availableUploaders: UploaderInfo[];
  /** 필터 초기화 핸들러 */
  onReset: () => void;
}

// ===== 상수 =====

const FILE_CATEGORIES: (FileCategory | '전체')[] = [
  '전체',
  '설계도면',
  '3D모델',
  '문서',
  '사진',
  'CT결과서',
  'QR코드',
  '기타',
];

/**
 * 파일 필터 패널 컴포넌트
 * 
 * @description
 * 파일 목록 필터링을 위한 다중 필터 UI
 * 
 * @filters
 * - 카테고리: 파일 카테고리별 필터링
 * - 업로더: 업로드한 사용자별 필터링
 * - 파일 크기: 1MB 이하, 1-10MB, 10MB 이상
 * - 태그: 다중 태그 AND 조건
 * - 즐겨찾기: 즐겨찾기 파일만 표시
 * 
 * @layout
 * - FileManagerPage 우측 30% 영역에 고정
 * - 최소 너비 280px 보장
 */
export default function FileFilterPanel({
  filters,
  onChange,
  availableUploaders,
  onReset,
}: FileFilterPanelProps) {
  // ===== State =====

  const [tagInput, setTagInput] = useState('');

  // ===== Handlers =====

  /**
   * 카테고리 변경
   */
  const handleCategoryChange = (category: FileCategory | '전체') => {
    onChange({
      ...filters,
      category,
    });
  };

  /**
   * 업로더 변경
   */
  const handleUploaderChange = (uploader: string | null) => {
    onChange({
      ...filters,
      uploader: uploader === '전체' ? null : uploader,
    });
  };

  /**
   * 크기 범위 변경
   */
  const handleSizeRangeChange = (range: string) => {
    let sizeRange: { min: number; max: number } | null = null;

    if (range === '1MB 이하') {
      sizeRange = { min: 0, max: 1024 * 1024 };
    } else if (range === '1-10MB') {
      sizeRange = { min: 1024 * 1024, max: 10 * 1024 * 1024 };
    } else if (range === '10MB 이상') {
      sizeRange = { min: 10 * 1024 * 1024, max: Number.MAX_SAFE_INTEGER };
    }

    onChange({
      ...filters,
      sizeRange,
    });
  };

  /**
   * 현재 선택된 크기 범위 라벨
   */
  const getSizeRangeLabel = (): string => {
    if (!filters.sizeRange) return '전체';
    
    const { min, max } = filters.sizeRange;
    
    if (max <= 1024 * 1024) return '1MB 이하';
    if (min >= 10 * 1024 * 1024) return '10MB 이상';
    if (min >= 1024 * 1024 && max <= 10 * 1024 * 1024) return '1-10MB';
    
    return '전체';
  };

  /**
   * 태그 추가
   */
  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !filters.tags.includes(trimmedTag)) {
      onChange({
        ...filters,
        tags: [...filters.tags, trimmedTag],
      });
      setTagInput('');
    }
  };

  /**
   * 태그 삭제
   */
  const handleRemoveTag = (tagToRemove: string) => {
    onChange({
      ...filters,
      tags: filters.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  /**
   * 즐겨찾기 토글
   */
  const handleFavoriteToggle = (checked: boolean) => {
    onChange({
      ...filters,
      onlyFavorites: checked,
    });
  };

  // ===== Render =====

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      {/* 헤더 */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <FilterIcon color="primary" />
          <Typography variant="h6">필터</Typography>
        </Box>
        <Button
          size="small"
          startIcon={<ResetIcon />}
          onClick={onReset}
        >
          초기화
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* 카테고리 필터 */}
      <Box mb={2}>
        <Typography variant="subtitle2" gutterBottom>
          카테고리
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          value={filters.category}
          onChange={(e) => handleCategoryChange(e.target.value as FileCategory | '전체')}
        >
          {FILE_CATEGORIES.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat === '전체' ? (
                <span>전체</span>
              ) : (
                <Box display="flex" alignItems="center" gap={1}>
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: 20,
                      color: CATEGORY_COLORS[cat as FileCategory],
                    }}
                  >
                    {CATEGORY_ICONS[cat as FileCategory]}
                  </span>
                  <span>{cat}</span>
                </Box>
              )}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* 업로더 필터 */}
      <Box mb={2}>
        <Typography variant="subtitle2" gutterBottom>
          업로더
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          value={filters.uploader || '전체'}
          onChange={(e) => handleUploaderChange(e.target.value === '전체' ? null : e.target.value)}
        >
          <MenuItem value="전체">전체</MenuItem>
          {availableUploaders.map((uploader) => (
            <MenuItem key={uploader.id} value={uploader.id}>
              <Box>
                <Typography variant="body2">{uploader.name}</Typography>
                {uploader.email && (
                  <Typography variant="caption" color="text.secondary">
                    {uploader.email}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* 크기 범위 필터 */}
      <Box mb={2}>
        <Typography variant="subtitle2" gutterBottom>
          파일 크기
        </Typography>
        <RadioGroup
          value={getSizeRangeLabel()}
          onChange={(e) => handleSizeRangeChange(e.target.value)}
        >
          <FormControlLabel
            value="전체"
            control={<Radio size="small" />}
            label="전체"
          />
          <FormControlLabel
            value="1MB 이하"
            control={<Radio size="small" />}
            label="1MB 이하"
          />
          <FormControlLabel
            value="1-10MB"
            control={<Radio size="small" />}
            label="1-10MB"
          />
          <FormControlLabel
            value="10MB 이상"
            control={<Radio size="small" />}
            label="10MB 이상"
          />
        </RadioGroup>
      </Box>

      {/* 태그 필터 */}
      <Box mb={2}>
        <Typography variant="subtitle2" gutterBottom>
          태그
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="태그 입력 후 Enter"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddTag();
            }
          }}
        />
        {filters.tags.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
            {filters.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={() => handleRemoveTag(tag)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* 즐겨찾기 필터 */}
      <Box mb={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.onlyFavorites}
              onChange={(e) => handleFavoriteToggle(e.target.checked)}
              size="small"
            />
          }
          label="즐겨찾기만 보기"
        />
      </Box>
    </Paper>
  );
}